RivalIQ.apps.Dashboard.module('curations.filters', function (filters, App, Backbone, Marionette, $, _) {

    'use strict';

    // NOTE the following is exposed for testing
    // it is meant to indicate the curations that should not be redirected to
    // companies when a landscape is empty (without a single followed company)
    filters._unguardedEmptyLandscapeCurations = [
        RivalIQ.enums.curation.COMPANIES,
        RivalIQ.enums.curation.ACCOUNT,
        RivalIQ.enums.curation.ACCOUNT_JOIN,
        RivalIQ.enums.curation.INVOICES
    ];

    filters.guardEmptyLandscape = function (curation) {
        const companies = RivalIQ.state.landscape.get('companies');
        const isGuarded = (filters._unguardedEmptyLandscapeCurations.indexOf(curation.id) < 0);
        if (isGuarded && _.size(companies) === 0) {
            return '/companies';
        }
    };

    filters.updateShell = function (curation) {
        // The dontUpdateShell is special handling for React curations.
        if (curation.isExporting() || curation.dontUpdateShell) {
            return;
        }

        // TODO these are very slow render steps
        // Don't change the title if the curation has been torn down
        curation.requestAnimationFrame(function () {
            RivalIQ.apps.Shell.execute('set:primary',
                curation.getNavigationTitle(),
                curation.getNavigationComponents());

            curation.requestAnimationFrame(function () {
                if (curation.presence && curation.getProp('insightsBlocked')) {
                    RivalIQ.apps.Shell.execute('hide:navigation');
                } else {
                    RivalIQ.apps.Shell.execute('set:navigation', {
                        page: _.result(curation, 'page'),
                        view: _.result(curation, 'secondaryNavigationView')
                    });
                }
            });
        });
    };

    filters.track = function (curation) {
        const tracking = curation.getViewTracking();

        if (tracking) {
            RivalIQ.execute('tracking:push:dynamic', tracking);
        }

        // Tell GA about the page view. We need to do this here after the curation has loaded because the curation
        // is what sets `document.title`.
        if (!RivalIQ.isHeadless && !RivalIQ.isImpersonating && RivalIQ.trackPageView) {
            const trackingPath = RivalIQ.state.product.get('urlBasepath') + document.location.pathname;

            RivalIQ.logger.info('GA pageview from in-app navigate: {0}', trackingPath);
            RivalIQ.execute('tracking:push:gtag', 'page_view', {
                page_title: document.title,
                page_path: trackingPath
            });

            RivalIQ.trackPageView = false;
        }
    };

    filters.showOnboardingModal = function (curation) {
        if (curation.isExporting()) {
            return;
        }

        if (RivalIQ.state.user.get('showOnboardingDialog') && RivalIQ.state.user.isActive() &&
            RivalIQ.state.account.isPaid() && !RivalIQ.state.view.get('isImpersonating')) {

            const options = {
                csm: RivalIQ.state.account.get('csm'),
                isNewPurchaser: false
            };
            RivalIQ.execute('show:modal', new RivalIQ.apps.Shell.views.OnboardingModal(options));
            RivalIQ.state.user.set('showOnboardingDialog', false);
        }
    };

    filters.watchViewState = function (curation) {
        const stateProps = curation.getStateProps();

        function listenToStateChange(state, updateStateOnChange) {
            curation.listenTo(state, 'change', function (state) {
                const changedProps = _.pick(state.changed, stateProps);
                // TODO clean up use of tag here
                if (updateStateOnChange && RivalIQ.state.utils.isOnlyCompanySet(state.changed.tag)) {
                    changedProps.focusCompanyId = RivalIQ.state.utils.getCompanyIdFromSetId(state.changed.tag);
                }

                curation.setGlobalProps(changedProps, {
                    navigate: false
                });
            });
        }

        listenToStateChange(RivalIQ.state.view, true);
        listenToStateChange(RivalIQ.state.landscape);
        listenToStateChange(RivalIQ.state.account);
        listenToStateChange(RivalIQ.state.user);
    };

    filters.initViewStateProps = function (curation) {
        const stateProps = curation.getStateProps();

        const state = _.extend(
            RivalIQ.state.plan.pick(stateProps),
            RivalIQ.state.user.pick(stateProps),
            RivalIQ.state.account.pick(stateProps),
            RivalIQ.state.landscape.pick(stateProps),
            RivalIQ.state.view.pick(stateProps)
        );

        // overwrite the focus company id based on company set
        state.focusCompanyId = RivalIQ.state.utils.getFocusedCompanyId();

        curation.setGlobalProps(state, {
            silent: true,
            navigate: false
        });
    };

    // If we're currently applying post tags, clear the cache on navigation so that every time the user hits a new page they get the latest data.
    filters.checkPostTagsCaches = function () {
        const postTagUpdateStatus = RivalIQ.state.landscape.get('postTagUpdateStatus');
        if (_.includes([RivalIQ.data.DataUpdateStates.UPDATE_IN_FLIGHT, RivalIQ.data.DataUpdateStates.UPDATE_COMPLETE], postTagUpdateStatus)) {
            RivalIQ.data.CacheLayer.invalidateAllCaches(false);
        }
    };

    filters.startBuffering = function () {
        RivalIQ.data.CacheLayer.startBuffering();
        RivalIQ.universal.startBuffering();
    };

    filters.endBuffering = function () {
        RivalIQ.universal.stopBuffering();

        // Future requests will be fetched on demand
        RivalIQ.data.CacheLayer.stopBuffering();

        RivalIQ.universal.flush();

        // Resolve all pending requests
        RivalIQ.data.CacheLayer.flush();
    };

    filters.prepareWidgetInstanceState = function (curation) {
        const configuration = _.get(curation, ['configuration']);
        if (!configuration) {
            return;
        }

        const definitions = configuration.get('definitions');
        // NOTE it is VERY important that we work around the `getWidgets`
        // apparatus, as we have a lifecycle limitation wherein we cannot do
        // both (i.e., we cannot call `getWidgets` and then load state, or the
        // relevant widget state won't be populated)
        const preparation = _.reduce(definitions, function (memo, definition) {
            const instanceId = definition.instanceId;
            const Widget = Marionette.Dashboard.widgets.get(definition.widgetId);
            if (instanceId && _.isFunction(Widget.prepare)) {
                const props = _.extend({}, curation.getProps(), definition.props);
                return memo.concat(
                    Widget.prepare(props).then(function (state) {
                        curation.setWidgetInstanceState(instanceId, state);
                    })
                );
            }
            return memo;
        }, []);

        return $.when.apply($, preparation).then(_.noop);
    };

    filters.clearPopovers = function () {
        const $popovers = $('.rivaliq-popover');
        $popovers.popover('destroy');
        RivalIQ.logger.info(`destroyed ${$popovers.length} popovers in curation filter`);
    };

    filters.checkForAdBlockers = function (curation) {
        if (!curation.isExporting() && !RivalIQ.state.account.isImpersonated()) {

            // Note: We're intentionally not returning a promise here; there's no need to block the app from starting
            // while we wait for the ad blocker check as it doesn't change functionality at all.
            RivalIQ.adBlockerDetector.checkAndShowAdBlockerNag();
        }
    };
});
